import { get, set } from 'lodash';
/**
 * Pure helpers deciding how a form field is rendered: which control, which
 * validation key, which CSS classes and which default value.
 */
/**
 * The validation key and the prefix of nested fields, e.g. `address.street`
 * or `items[2].name`.
 */
export function fieldPath(prefix, name) {
    return prefix ? prefix + '.' + name : name;
}
export function collectionItemPrefix(prefix, index) {
    return prefix + '[' + index + ']';
}
/**
 * Field names may contain periods, which are not usable in CSS classes and ids.
 */
export function escapeClass(value) {
    return value.replace(/\./g, '-');
}
/**
 * `options.localized` is also set on non-text fields (e.g. product attributes of
 * type choice or number); only text-ish fields get the per-locale control.
 */
export function isLocalizable(type) {
    switch (type) {
        case 'text':
        case 'textarea':
        case 'html':
        case 'code':
            return true;
    }
    return false;
}
export function resolveFieldControl(field) {
    const opts = field.options || {};
    if (opts.localized && isLocalizable(field.type)) {
        return 'localized';
    }
    switch (field.type) {
        case 'textarea':
        case 'code':
            return 'textarea';
        case 'html':
            return 'html';
        case 'json':
            return 'json';
        case 'bool':
            return 'boolean';
        case 'choice':
        case 'country':
            return 'choice';
        case 'date':
            return 'date';
        case 'datetime':
        case 'datetime_local':
            return 'datetime';
        case 'entity':
            return 'entity';
        case 'number':
        case 'currency':
            return 'number';
        case 'form':
            return 'form';
        case 'table':
            return 'table';
        case 'file':
            return 'file';
        case 'range_time':
            return 'range_time';
        case 'info':
            return 'info';
        case 'password':
            return 'password';
        case 'secret':
            return 'secret';
        case 'credentials':
            return 'credentials';
        default:
            return 'input';
    }
}
/**
 * The `type` attribute of the plain `<input>` control.
 */
export function inputType(type) {
    switch (type) {
        case 'text':
            return 'text';
        case 'date':
            return 'date';
        case 'datetime':
            return 'datetime-local';
        case 'time':
            return 'time';
        case 'email':
            return 'email';
        case 'url':
            return 'url';
        case 'password':
            return 'password';
    }
    throw 'Unsupported field type: ' + type;
}
export function isSelectionField(field, control) {
    return (control === 'choice' && !!field.options && !field.options.expanded)
        || field.type === 'entity'
        || (control === 'boolean' && !!field.options && !field.options.required);
}
export function groupClasses(field, control, state) {
    const options = field.options || {};
    const classes = [];
    if (options.required && control !== 'boolean') {
        classes.push('form__group--required');
    }
    classes.push('form__group--field-' + control);
    classes.push('form__group--field-' + field.type);
    classes.push('form__group--attribute-' + escapeClass(field.name));
    if (control === 'choice' && options.expanded) {
        classes.push('form__group--choice-expanded');
    }
    if (control === 'boolean' && !options.required) {
        classes.push('form__group--field-boolean-select');
    }
    if (state.focussed) {
        classes.push('form__group--active');
    }
    if (state.invalid) {
        classes.push('form__group--invalid');
    }
    if (state.mode === 'bulk' && options.disabled) {
        classes.push('form__group--bulk-ignored');
    }
    return classes;
}
/**
 * The value an empty field is initialized with, or `undefined` if it stays empty:
 * nested forms and sections with inner fields nest their data, required
 * booleans default to `false`.
 */
export function defaultValue(field) {
    if (field.type === 'form') {
        return field.options?.collection ? [] : {};
    }
    if (field.type === 'section' && field.fields && field.fields.length > 0) {
        return {};
    }
    if (field.type === 'bool' && field.options?.required) {
        return false;
    }
    return undefined;
}
/**
 * Sets the default value of every empty field that has one. Returns whether
 * anything was written.
 */
export function applyDefaults(data, fields) {
    if (!data) {
        return false;
    }
    let applied = false;
    fields.forEach(field => {
        const value = get(data, field.name);
        if (value !== null && value !== undefined) {
            return;
        }
        const initial = defaultValue(field);
        if (initial !== undefined) {
            set(data, field.name, initial);
            applied = true;
        }
    });
    return applied;
}
